import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ClipboardCheck, Heart, UserPlus, Languages } from "lucide-react";

// thedisciple.kiosk — a tablet-parked launcher for the church lobby. Three big
// touch targets: Attendance, Prayer, Guest sign-up. Each deep-links to the
// existing check-in surface for this org so guests never bounce between apps.
// Designed for a tablet mounted at 1024x768+; buttons are 100mm+ tap targets.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

const LANG_KEY = "kiosk-lang";
const STRINGS = {
  en: {
    brand: "kiosk",
    tap: "Tap what you'd like to do.",
    time: (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
    date: (d) => d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" }),
    tiles: {
      attendance: { label: "Attendance", caption: "Check in to today's service" },
      prayer:     { label: "Prayer",     caption: "Log a visit to pray" },
      signup:     { label: "Guest Sign-up", caption: "New here? Say hi." },
    },
    open: "Open →",
    tap_pill: "Tap",
    off_pill: "Off",
    prayer_off: "Prayer isn't turned on yet",
    footer_left: "the disciple .life · Kiosk mode",
    footer_right: "Powered by the ministry",
    err_title: "Kiosk not ready",
    err_hint: "Ask a church leader to set up the attendance QR code first.",
    lang_switch: "Español",
  },
  es: {
    brand: "kiosco",
    tap: "Toca lo que te gustaría hacer.",
    time: (d) => d.toLocaleTimeString("es-ES", { hour: "numeric", minute: "2-digit" }),
    date: (d) => d.toLocaleDateString("es-ES", { weekday: "long", month: "long", day: "numeric" }),
    tiles: {
      attendance: { label: "Asistencia", caption: "Registra tu asistencia al servicio de hoy" },
      prayer:     { label: "Oración",    caption: "Registra una visita para orar" },
      signup:     { label: "Nuevo Visitante", caption: "¿Primera vez? Salúdanos." },
    },
    open: "Abrir →",
    tap_pill: "Toca",
    off_pill: "Apagado",
    prayer_off: "Oración aún no está activada",
    footer_left: "the disciple .life · Modo kiosco",
    footer_right: "Con el respaldo del ministerio",
    err_title: "Kiosco no está listo",
    err_hint: "Pídele a un líder que configure el código QR de asistencia primero.",
    lang_switch: "English",
  },
};

export default function Kiosk() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, err: "", org: null, links: null });
  const [now, setNow] = useState(() => new Date());
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(LANG_KEY) === "es" ? "es" : "en"; } catch { return "en"; }
  });
  const t = STRINGS[lang];

  useEffect(() => {
    api.get(`/public/kiosk/${code}`)
      .then((r) => setState({ loading: false, err: "", org: r.data.organization, links: r.data.links }))
      .catch((e) => setState({ loading: false, err: e?.response?.data?.detail || t.err_hint, org: null, links: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(timer);
  }, []);

  const toggleLang = () => {
    const next = lang === "en" ? "es" : "en";
    setLang(next);
    try { localStorage.setItem(LANG_KEY, next); } catch { /* private mode */ }
    window.dispatchEvent(new Event("kiosk-lang"));
  };

  // Append kiosk return params so the child page can auto-return here.
  const withKioskReturn = (path) => {
    if (!path) return null;
    const sep = path.includes("?") ? "&" : "?";
    return `${path}${sep}from=kiosk&kc=${code}&lang=${lang}`;
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-base)", color: "var(--muted)" }}>
        <p className="text-sm">Loading…</p>
      </div>
    );
  }

  if (state.err) {
    return (
      <div className="min-h-screen flex items-center justify-center px-8" style={{ background: "var(--bg-base)", color: "var(--ink)" }}>
        <div className="max-w-md text-center" data-testid="kiosk-error">
          <h1 className="font-serif-display text-3xl mb-3">{t.err_title}</h1>
          <p className="text-sm mt-4" style={{ color: "var(--muted)" }}>{state.err || t.err_hint}</p>
        </div>
      </div>
    );
  }

  const tiles = [
    { key: "attendance", ...t.tiles.attendance, icon: ClipboardCheck, to: withKioskReturn(state.links.attendance), testid: "kiosk-tile-attendance" },
    { key: "prayer",     ...t.tiles.prayer,     icon: Heart,          to: withKioskReturn(state.links.prayer),     disabled: !state.links.prayer, disabledNote: t.prayer_off, testid: "kiosk-tile-prayer" },
    { key: "signup",     ...t.tiles.signup,     icon: UserPlus,       to: withKioskReturn(state.links.signup),     testid: "kiosk-tile-signup" },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-base)", color: "var(--ink)" }} data-testid="kiosk-page">
      {/* Header */}
      <header className="px-8 sm:px-12 pt-10 pb-6 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[0.65rem] uppercase tracking-[0.32em] font-semibold mb-1" style={{ color: "var(--gold)" }}>
            thedisciple<span style={{ color: "var(--ink)" }}>.{t.brand}</span>
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight" style={{ color: "var(--ink)" }}>{state.org.name}</h1>
          <p className="text-sm mt-2" style={{ color: "var(--ink-2)" }}>{t.tap}</p>
        </div>
        <div className="flex items-start gap-4">
          <button
            onClick={toggleLang}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.18em] rounded-full transition-colors"
            style={{ color: "var(--gold)", border: "1px solid var(--gold)", background: "transparent" }}
            data-testid="kiosk-lang-toggle"
            aria-label="Switch language"
          >
            <Languages size={13} />
            <span>{t.lang_switch}</span>
          </button>
          <div className="text-right">
            <div className="font-serif-display text-4xl" style={{ color: "var(--ink)" }}>{t.time(now)}</div>
            <div className="text-[0.65rem] uppercase tracking-[0.28em]" style={{ color: "var(--muted)" }}>{t.date(now)}</div>
          </div>
        </div>
      </header>

      {/* Tiles */}
      <main className="flex-1 flex items-center px-8 sm:px-12 pb-16">
        <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.key}
                onClick={() => !tile.disabled && tile.to && navigate(tile.to)}
                disabled={tile.disabled || !tile.to}
                className="group rounded-2xl p-10 text-left transition-transform"
                style={{
                  background: "var(--bg-alt)",
                  border: `1.5px solid ${tile.disabled ? "var(--border-subtle)" : "var(--gold)"}`,
                  boxShadow: tile.disabled ? "none" : "0 20px 40px -20px rgba(0,0,0,0.5), inset 0 0 0 3px var(--bg-base)",
                  minHeight: "18rem",
                  opacity: tile.disabled ? 0.55 : 1,
                  cursor: tile.disabled ? "not-allowed" : "pointer",
                }}
                data-testid={tile.testid}
              >
                <div className="flex items-center justify-between mb-8">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ background: "rgba(230,184,92,0.12)", color: "var(--ember)", border: "1px solid rgba(230,184,92,0.35)" }}
                  >
                    <Icon size={30} strokeWidth={1.75} />
                  </div>
                  <span className="text-[0.6rem] uppercase tracking-[0.28em]" style={{ color: "var(--muted)" }}>
                    {tile.disabled ? t.off_pill : t.tap_pill}
                  </span>
                </div>
                <div className="font-serif-display text-4xl leading-tight mb-2" style={{ color: "var(--ink)" }}>{tile.label}</div>
                <div className="text-sm" style={{ color: "var(--ink-2)" }}>
                  {tile.disabled ? tile.disabledNote : tile.caption}
                </div>
                {!tile.disabled && (
                  <div className="mt-6 text-[0.6rem] uppercase tracking-[0.28em]" style={{ color: "var(--gold)" }}>{t.open}</div>
                )}
              </button>
            );
          })}
        </div>
      </main>

      {/* Footer */}
      <footer className="px-8 sm:px-12 pb-8 flex items-center justify-between text-[0.6rem] uppercase tracking-[0.28em]" style={{ color: "var(--muted)" }}>
        <span>{t.footer_left}</span>
        <span>{t.footer_right}</span>
      </footer>
    </div>
  );
}
